export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  requiredG1G2Sum?: number; // Helper hint: what G1+G2 should add up to
}

export function CustomRailValidation(
  length: number,
  g1: number,
  g2: number,
  holePitch: number,
  holeDiameter: number,
  minLength: number,
  maxLength: number,
  lengthIncrement: number,
  g1Min: number,
  g1Max: number,
  g1Increment: number,
  g2Min: number,
  g2Max: number,
  g2Increment: number
): ValidationResult {
  const errors: ValidationError[] = [];

  // Validate length range
  if (length < minLength || length > maxLength) {
    errors.push({
      field: 'length',
      message: `Rail length must be between ${minLength}mm and ${maxLength}mm`
    });
  }

  // Validate length increment
  if (!isValidIncrement(length, minLength, lengthIncrement)) {
    errors.push({
      field: 'length',
      message: `Rail length must follow ${lengthIncrement}mm increments from ${minLength}mm`
    });
  }

  // Validate G1 range
  if (g1 < g1Min || g1 > g1Max) {
    errors.push({
      field: 'g1',
      message: `G1 must be between ${g1Min}mm and ${g1Max}mm`
    });
  }

  // Validate G1 increment
  if (!isValidIncrement(g1, g1Min, g1Increment)) {
    errors.push({
      field: 'g1',
      message: `G1 must follow ${g1Increment}mm increments from ${g1Min}mm`
    });
  }

  // Validate G2 range
  if (g2 < g2Min || g2 > g2Max) {
    errors.push({
      field: 'g2',
      message: `G2 must be between ${g2Min}mm and ${g2Max}mm`
    });
  }

  // Validate G2 increment
  if (!isValidIncrement(g2, g2Min, g2Increment)) {
    errors.push({
      field: 'g2',
      message: `G2 must follow ${g2Increment}mm increments from ${g2Min}mm`
    });
  }

  const requiredG1G2Sum = calculateGDimension(length, holePitch, holeDiameter, minLength, maxLength); // This will return undefined if length is out of range, but we already check that above

  // Validate geometric formula: G1 + G2 + (n × F) = L
  if (!validateGeometry(length, g1, g2, holePitch, requiredG1G2Sum)) {
    errors.push({
      field: 'geometry',
      message: `Invalid configuration: G1 + G2 must equal ${requiredG1G2Sum}mm when Length is ${length}mm`
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    requiredG1G2Sum // Return this for the helper hint
  };
}

function isValidIncrement(value: number, min: number, increment: number): boolean {
  if (increment === 0) return value === min;
  const steps = (value - min) / increment;
  return Math.abs(steps - Math.round(steps)) < 0.0001;
}

function validateGeometry(length: number, g1: number, g2: number, holePitch: number, requiredG1G2Sum: number | undefined): boolean {
  if(requiredG1G2Sum === undefined) return true;
  
  const remainder = length - g1 - g2;
  if (remainder < 0) return false;
  if (holePitch === 0) return remainder === 0;

  const numberOfHoles = remainder / holePitch;
  return Math.abs(numberOfHoles - Math.round(numberOfHoles)) < 0.0001;
}

/**
 * Calculates the G dimension (G1 + G2 combined end overhang) for a custom-length linear guide rail.
 *
 * @param railLength      - Customer-requested rail length L (column G)
 * @param holePitch   - Hole pitch F (column C)
 * @param holeDiameter  - Counterbore diameter d2 (column D)
 * @param minRailLength   - Minimum allowed rail length (column E)
 * @param maxRailLength   - Maximum allowed rail length (column F)
 * @returns The G dimension (G1 + G2) in mm, or undefined if the input is out of range
 */
function calculateGDimension(
  railLength: number,
  holePitch: number,
  holeDiameter: number,
  minRailLength: number,
  maxRailLength: number
): number | undefined {
  // Step 1: Validate rail length is within allowed range
  if (railLength > maxRailLength) {
    return undefined; // Rail too long
  }

  if (railLength <= minRailLength - 0.1) {
    return undefined; // Rail too short
  }

  // Step 2: Calculate the remainder (overhang) from the hole pitch pattern
  //         This is equivalent to: railLength mod holePitch
  const remainder = (railLength / holePitch - Math.floor(railLength / holePitch)) * holePitch;

  // Step 3: Check if the overhang clears the hole diameter + 4mm margin
  if (remainder > holeDiameter + 4) {
    return Math.round(remainder);       // Overhang is large enough — use as-is
  }  
  
  return Math.round(remainder + holePitch); // Too small — add one full pitch interval  
}